import { useSceneStore } from '@/features/shared/stores/scene.store'
import { getSceneHeader } from '@/shared/utils/editor-utils'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $createTextNode, $getNodeByKey, ElementNode } from 'lexical'
import { forwardRef, useEffect } from 'react'
import { Position, SuggestionListItem } from '../type'
import { sceneSuggestions, transitionSuggestions } from './constants'
import { NodeType } from './nodes/NodeCore'
import { SceneNode } from './nodes/SceneNode'
import { useSpEditorProvider } from './SpEditorProvider'
import { SuggestionDropdown } from './SuggestionMenu'
import { getCurrentNode } from './utils/NodeUtils'

interface SuggestionBoxProps {
    position: Position
    editor: ReturnType<typeof useLexicalComposerContext>[0]
}

const ZERO_WIDTH_RE = /(?:\u200B|\u200C|\u200D|\uFEFF|\u2060|\u180E)/gu

const normalizeText = (text: string) => {
    return text
        .replace(ZERO_WIDTH_RE, '')
        .normalize('NFKC')
        .trim()
        .replace(/\s+/g, ' ')
}

const getCharacterOptions = (characters: any[]): SuggestionListItem[] => {
    return characters.map((c) => ({
        ...c,
        title: normalizeText(c.name),
    })) as SuggestionListItem[]
}

const getSceneOptions = (scenes: SceneNode[], editor: any): SuggestionListItem[] => {
    const existingScenes = scenes
        .map((s) =>
            editor.getEditorState().read(() => {
                const latestNode = $getNodeByKey(s.getKey())
                return latestNode ? getSceneHeader(latestNode as SceneNode) : null
            })
        )
        .filter(Boolean) as string[]

    const uniqueOptions = new Map<string, SuggestionListItem>()

    const addOption = (title: string) => {
        const normalized = normalizeText(title)
        if (normalized && normalized !== '[No Title]') {
            const key = normalized.toUpperCase()
            if (!uniqueOptions.has(key)) {
                uniqueOptions.set(key, { title: normalized })
            }
        }
    }

    sceneSuggestions.forEach((s) => addOption(s.title))
    existingScenes.forEach((title) => addOption(title))

    return Array.from(uniqueOptions.values())
}

const getTransitionOptions = (): SuggestionListItem[] => {
    return transitionSuggestions.map((s) => ({
        ...s,
        title: normalizeText(s.title),
    }))
}

const SpSuggestionBox = forwardRef<HTMLDivElement, SuggestionBoxProps>(({ editor, position }, ref) => {
    const { setSuggestionProps } = useSpEditorProvider()
    const nodeType = editor.getEditorState().read(() => {
        return getCurrentNode()?.getType() as NodeType
    })
    const { characters } = useSpEditorProvider()
    const { scenes } = useSceneStore()

    useEffect(() => {
        let options: SuggestionListItem[] = []

        switch (nodeType) {
            case NodeType.Character:
                options = getCharacterOptions(characters)
                break
            case NodeType.Scene:
                options = getSceneOptions(scenes, editor)
                break
            case NodeType.Transition:
                options = getTransitionOptions()
                break
        }

        setSuggestionProps((prev) => ({
            ...prev,
            options,
        }))
    }, [nodeType, characters, scenes, editor])

    return (
        <SuggestionDropdown
            ref={ref}
            onSelect={(c) => {
                editor.update(() => {
                    const node = getCurrentNode() as ElementNode
                    node?.append($createTextNode(c.title))
                    node?.selectEnd()
                })
            }}
        />
    )
})

export default SpSuggestionBox
